import Close from "@mui/icons-material/Close";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
	Alert,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormControl,
	Grid,
	IconButton,
	Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import _ from "lodash";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useDrawerTriggerNotification, useRecommendationsDrawerContext } from "../../contexts/RecommendationsDrawerContext";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSelfScheduling } from "../../contexts/SelfScheduling/SelfSchedulingContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useSidePanelHost } from "../../contexts/sidePanel/SidePanelHostContext";
import type { SidePanelHost } from "../../contexts/sidePanel/sidePanelTypes";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useElementHeight } from "../../hooks/useElementHeight";
import { useFormatPersonName, useLastNameFirst } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { HOSPITAL_DEMO, INK, INK_BLACK, NEUTRAL, STATUS, STATUS_BADGE, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { getStaffingIssues, type StaffingIssue } from "../../utils/autoStaffing";
import { formatPersonFullName, isNurseActiveInDepartment } from "../../utils/formatters";
import type { Nurse, NurseFullRole, NurseRole } from "../../utils/models";
import { IssuePriority, RecommendationOpenSource, type Shift } from "../../utils/models";
import { countRequestsWithResponses, inverseRolesMap, rolesMap } from "../../utils/utilFunctions";
import { DrawerTriggerButton } from "../DrawerTriggerButton/DrawerTriggerButton";
import { DateSlider } from "../dateSlider";
import EmploymentTypeSelector from "../employmentTypeSelector/EmploymentTypeSelector";
import { HeaderButton } from "../headerButton/HeaderButton";
import { SelfSchedulingButton } from "../ImportSelfSchedulingButton/SelfSchedulingButton";
import { DRAWER_WIDTH_IN_PX } from "../RecommendationsDrawer/utils";
import { StaffingBlock } from "../staffingBlock";
import { TEAM_SECTIONS } from "../team/config";
import { StaffAutoComplete } from "../UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import { RoleOrJobSelector } from "../UIToolkit/molecules/RoleOrJobSelector";
import { Availability } from "./Availability";
import { DraftMode } from "./DraftMode";
import MoonIcon from "./moon-icon.svg";
import { ScheduleExportButton } from "./ScheduleExport/ScheduleExportButton";
import { ScheduleVisibilityButton } from "./Settings/ScheduleVisibilityButton";
import { StaffingSettingsPanel } from "./Settings/StaffingSettings";
import { StaffScheduleVisibilityPopup } from "./Settings/StaffScheduleVisibilityPopup";
import Style from "./Staffing.module.scss";
import SunIcon from "./sun-icon.svg";

const shiftsTypeCount = 2;

const Main = styled("div", { shouldForwardProp: (prop) => prop !== "open" })<{
	open?: boolean;
}>(({ theme, open }) => ({
	flexGrow: 1,
	padding: 0,
	minHeight: "calc(63%)",
	maxHeight: "calc(56vh)",
	paddingBottom: "10px",
	transition: theme.transitions.create(["margin", "width"], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginRight: `${DRAWER_WIDTH_IN_PX}px`,
		width: `calc(100% - ${DRAWER_WIDTH_IN_PX}px)`,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

// Add this style to position the shift label container
const ShiftLabelContainer = styled("div", { shouldForwardProp: (prop) => prop !== "demo" })<{ demo: boolean }>(({ theme, demo }) => ({
	position: "relative",
	display: "flex",
	alignItems: "center",
	marginRight: "10px",
	width: "50px",
	borderRadius: "4px",
	// Demo dark-green Day/Night bar; others keep the light primary tint.
	background: demo ? HOSPITAL_DEMO.GREEN_DARK : alpha(theme.palette.primary.light, 0.3),
	marginTop: "20px",
	height: "calc(100% - 16px)",
}));

const TopBar = styled("div")({
	display: "flex",
	alignItems: "center",
	gap: "1px",
	backgroundColor: SURFACE,
	borderBottom: `1px solid ${NEUTRAL[300]}`,
});

export const TopRightPane = styled("div")({
	marginLeft: "auto",
	display: "flex",
	alignItems: "center",
	minWidth: "fit-content",
	"& *": {
		marginLeft: "-3px",
	},
});

const ShiftForm = styled(FormControl)({
	minWidth: "110px",
	marginRight: "10px",
	"& .MuiSelect-select": {
		padding: "8px 14px",
	},
});

export const Staffing: FC = () => {
	const { t } = useTranslation();
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { isDataInSelfSchedulingPeriod } = useSelfScheduling();

	const {
		hospitalData,
		acceptNurseChanges,
		declineNurseChanges,
		chosenDepartment,
		implementedChanges,
		setImplementedChanges,
		isSnf,
		isHospital,
		isHospitalDemo,
		isHospitalView,
		handleSuggestion,
		isAutoScheduler,
		timezone,
		departmentNurseTypes,
		calculateShiftTimes,
	} = useHospitalData();
	const [isApproveAll, setApproveAll] = useState(false);
	const topBarRef = useRef<HTMLDivElement>(null);
	const topBarHeight = useElementHeight(topBarRef);
	const { showSnackbar } = useSnackbar();
	const { isOpen: open, toggleDrawerOpen } = useRecommendationsDrawerContext();
	const { getDrawerTriggerNotification } = useDrawerTriggerNotification();
	const [chosenLevel, setChosenLevel] = useState<IssuePriority>(IssuePriority.TopPriority);
	const [recommendations, setRecommendations] = useState<Array<StaffingIssue>>([]);
	const prevRecommendationsRef = useRef<Array<StaffingIssue>>([]);
	const firstDate = hospitalData?.weeks[0]?.weeklyShifts[0]?.days[0]?.date;
	const startingDate = firstDate ? firstDate : DateTime.now();
	const endingDate = hospitalData?.weeks[hospitalData.weeks.length - 1]?.weeklyShifts[0]?.days[6]?.date
		? hospitalData?.weeks[hospitalData.weeks.length - 1]?.weeklyShifts[0]?.days[6]?.date
		: DateTime.now();
	const [isRequestedTab, setIsRequestedTab] = useState(false);
	const [isSmallScreen, setIsSmallScreen] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const [selectedShift, setSelectedShift] = useState<string[]>(hospitalData?.weeks[weekIndex]?.weeklyShifts.map((shift: Shift) => shift.type));
	const [availabilityLoaded, setAvailabilityLoaded] = useState(false);
	const [staffScheduleVisibilityOpen, setStaffScheduleVisibilityOpen] = useState(false);
	const [showStaffVisibilityBanner, setShowStaffVisibilityBanner] = useState<boolean>(true);

	const { staffRequests } = useRequestsContext();
	const isBlockRecommendationsV2Enabled = useFeatureFlag(FEATURE_FLAGS.BlockRecommendationsV2);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const lastNameFirst = useLastNameFirst();
	const requestsWithResponsesCount = useMemo(() => countRequestsWithResponses(staffRequests), [staffRequests]);
	const notification = getDrawerTriggerNotification(requestsWithResponsesCount);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const openNotifications = searchParams.get("openNotifications");
		const requestId = searchParams.get("requestId");

		if (openNotifications === "true") {
			toggleDrawerOpen(true, RecommendationOpenSource.WeeklyView);
			setIsRequestedTab(true);
			setChosenLevel(IssuePriority.Optional);
			// Remove the query parameter after opening the drawer
			navigate(location.pathname, { replace: true });
		} else if (requestId) {
			toggleDrawerOpen(true, RecommendationOpenSource.WeeklyView, { tab: "requests", requestId });
			setIsRequestedTab(true);
			setChosenLevel(IssuePriority.Optional);
			// Remove the query parameter after opening the drawer
			const newSearchParams = new URLSearchParams(location.search);
			newSearchParams.delete("requestId");
			const newSearch = newSearchParams.toString();
			navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
		}
	}, [location, navigate, toggleDrawerOpen]);

	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.innerWidth < 1420); // Adjust this value as needed
		};

		window.addEventListener("resize", handleResize);
		handleResize(); // Call once to set initial state

		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (!staffRequests || isRequestedTab || (isHospital && !availabilityLoaded && !isAutoScheduler)) {
			return;
		}

		if (!isBlockRecommendationsV2Enabled) {
			return;
		}

		if (hospitalData?.weeks?.length && hospitalData?.nurses?.length) {
			const newRecommendations = getStaffingIssues({
				hospitalData,
				weekIndex,
				isSnf,
				isAutoScheduler,
				chosenDepartment,
				timezone,
				departmentNurseTypes,
				requests: staffRequests,
				calculateShiftTimes,
				jobManagementEnabled,
				lastNameFirst,
			});
			if (!_.isEqual(newRecommendations, prevRecommendationsRef.current)) {
				prevRecommendationsRef.current = newRecommendations;
				setRecommendations(newRecommendations);
			}
		}
	}, [
		hospitalData,
		weekIndex,
		chosenDepartment,
		recommendations,
		availabilityLoaded,
		isSnf,
		isHospital,
		isRequestedTab,
		staffRequests,
		calculateShiftTimes,
		jobManagementEnabled,
		lastNameFirst,
	]);

	const [dialogOpen, setDialogOpen] = useState(false);

	const handleOpenDialog = useCallback(() => {
		setDialogOpen(true);
		trackEvent("approve_all_dialog_opened");
	}, []);

	const handleCloseDialog = () => {
		setDialogOpen(false);
		trackEvent("approve_all_dialog_cancelled");
	};

	// add 1 day to include ending date and remove week before
	const maxWeeks: number = endingDate.plus({ day: 1 }).diff(startingDate, "week").weeks - 1;

	const handleApproveAll = async () => {
		handleCloseDialog();
		trackEvent("Staffing - auto-staffing button clicked", {
			week_index: weekIndex,
			date_range: `${hospitalData?.weeks[weekIndex]?.weeklyShifts[0]?.days[0]?.date?.toFormat("yyyy-MM-dd")} to ${hospitalData?.weeks[weekIndex]?.weeklyShifts[0]?.days[6]?.date?.toFormat("yyyy-MM-dd")}`,
			auto_scheduler_version: "old",
			is_auto_scheduler: isAutoScheduler,
		});

		setApproveAll(true);
	};

	const [autoSchedulerCounterOldVersion, setAutoSchedulerCounterOldVersion] = useState(0);

	useEffect(() => {
		if (!isApproveAll) {
			return;
		}

		if (recommendations.filter((r) => r.priority === IssuePriority.TopPriority && r.suggestions.length > 0).length > 0) {
			new Promise((resolve) => setTimeout(resolve, 100)).then(() => {
				const rec = recommendations.filter((r) => r.priority === IssuePriority.TopPriority && r.suggestions.length > 0)[0];
				const suggestion = rec.suggestions[0];
				const changeKey = `${rec.title}-${suggestion.action}-${weekIndex}`;
				const shiftNo = suggestion.shiftNo;
				const dayNo = suggestion.dayNo;

				trackEvent("auto_staffing_suggestion_implemented", {
					title: rec.title,
					action: suggestion.action,
					week_index: weekIndex,
					shift_no: shiftNo,
					day_no: dayNo,
					priority: rec.priority,
				});

				handleSuggestion(suggestion, weekIndex, shiftNo, dayNo);
				setImplementedChanges((prev) => ({ ...prev, [changeKey]: true }));

				trackEvent("schedule_change_implemented");
			});
		} else {
			if (isAutoScheduler) {
				if (autoSchedulerCounterOldVersion < maxWeeks) {
					const nextWeekIndex = (weekIndex + 1) % (maxWeeks + 1);

					setWeekIndex(nextWeekIndex);

					setRecommendations(
						getStaffingIssues({
							hospitalData,
							weekIndex: nextWeekIndex,
							isSnf,
							isAutoScheduler: true,
							chosenDepartment,
							timezone,
							departmentNurseTypes,
							requests: staffRequests,
							calculateShiftTimes,
							jobManagementEnabled,
							lastNameFirst,
						}),
					);
					setAutoSchedulerCounterOldVersion((prevCounter) => prevCounter + 1);
				} else {
					setApproveAll(false);
					setAutoSchedulerCounterOldVersion(0); // Reset the counter
				}
			} else {
				setApproveAll(false);
			}
		}
	}, [isApproveAll, recommendations, weekIndex, jobManagementEnabled, lastNameFirst]);

	const handleDrawerOpen = () => {
		toggleDrawerOpen(true, RecommendationOpenSource.WeeklyView);
		trackEvent("recommendations_drawer_opened", {
			week_index: weekIndex,
			week_start_date: hospitalData?.weeks[weekIndex]?.weeklyShifts[0]?.days[0]?.date?.toFormat("yyyy-MM-dd"),
			recommendations_count: recommendations.length,
		});
	};

	const handleDrawerClose = useCallback(() => {
		toggleDrawerOpen(false, RecommendationOpenSource.WeeklyView);
		trackEvent("recommendations_drawer_closed", {
			week_index: weekIndex,
			tab: isRequestedTab ? "requested" : "recommendations",
		});
	}, [toggleDrawerOpen, weekIndex, isRequestedTab]);
	const [rolesToDisplay, setRolesToDisplay] = useState<Array<NurseFullRole>>(departmentNurseTypes);
	const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
	const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set(TEAM_SECTIONS.map((section) => section.title)));

	// RoleOrJobSelector emits short NurseRole + "all"; this screen tracks roles as NurseFullRole[].
	// Empty selection or "all" means "all department nurse types" downstream.
	const selectedRolesForSelector = useMemo<(NurseRole | "all")[]>(() => {
		if (rolesToDisplay.length === 0 || rolesToDisplay.length === departmentNurseTypes.length) return ["all"];
		return rolesToDisplay.map((fullRole) => inverseRolesMap[fullRole]).filter((r): r is NurseRole => Boolean(r));
	}, [rolesToDisplay, departmentNurseTypes]);

	const handleSelectedRolesChanged = useCallback(
		(selected: (NurseRole | "all")[]) => {
			if (selected.length === 0 || selected.includes("all")) {
				setRolesToDisplay(departmentNurseTypes);
				return;
			}
			setRolesToDisplay(
				selected
					.filter((r) => r !== "all")
					.map((r) => rolesMap[r])
					.filter((r): r is NurseFullRole => Boolean(r)),
			);
		},
		[departmentNurseTypes],
	);
	const employmentTypesToDisplay = useMemo(
		() => TEAM_SECTIONS.filter((section) => selectedSections.has(section.title)).flatMap((section) => section.employmentTypes),
		[selectedSections],
	);

	const [searchedNurse, setSearchedNurse] = useState<SystemAutoCompleteOption<Nurse> | null>(null);

	const formatPersonName = useFormatPersonName();
	const nurseOptions = useMemo<SystemAutoCompleteOption<Nurse>[]>(
		() =>
			hospitalData.nurses
				.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
				.map((nurse) => ({
					label: formatPersonName(nurse),
					value: nurse,
				})),
		[hospitalData.nurses, chosenDepartment.department_id, formatPersonName],
	);

	const [webSocketMessages, setWebSocketMessages] = useState<string[]>([]);

	const getDatAndWeekFromDate = (date: string): { weekIdx: number; dayNo: number } => {
		const dateParts = date.split(".");
		const month = Number.parseInt(dateParts[1], 10);
		const day = Number.parseInt(dateParts[0], 10);
		const year = Number.parseInt(dateParts[2], 10);
		const startDate = startingDate.toJSDate();
		const currentDate = new Date(year, month - 1, day);
		const diffTime = Math.abs(currentDate.getTime() - startDate.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		const weekIdx = Math.floor(diffDays / 7);
		const dayNo = diffDays % 7;
		return { weekIdx, dayNo };
	};

	const isValidJSON = (str: string): boolean => {
		try {
			JSON.parse(str);
			return true;
		} catch (e) {
			return false;
		}
	};

	useEffect(() => {
		const lastMessage = webSocketMessages[webSocketMessages.length - 1];
		if (!isValidJSON(lastMessage)) {
			return;
		}
		try {
			const change = JSON.parse(lastMessage);
			const { weekIdx, dayNo } = getDatAndWeekFromDate(change.shift_date);
			const shiftNo = change.shift_time;
			const nurseData = hospitalData?.nurses.find((n: Nurse) => n.id === change.user_id);
			if (!nurseData) return;

			if (change.response_meaning === "accept") {
				acceptNurseChanges(shiftNo, dayNo, change.user_id, weekIdx, false);
				showSnackbar(
					t("nurse.accepted.shift", {
						nurseName: formatPersonName(nurseData),
					}),
					"success",
				);
				trackEvent("nurse_accepted_shift", {
					nurse_id: change.user_id,
					// Analytics keep the stable "First Last" form whatever the department's display order is.
					nurse_name: formatPersonFullName(nurseData, false),
					shift_date: change.shift_date,
					week_index: weekIdx,
					day_no: dayNo,
					shift_no: shiftNo,
				});
			}
			if (change.response_meaning === "decline") {
				declineNurseChanges(shiftNo, dayNo, change.user_id, weekIdx, isAutoScheduler);
				showSnackbar(
					t("nurse.declined.shift", {
						nurseName: formatPersonName(nurseData),
					}),
					"error",
				);
				trackEvent("nurse_declined_shift", {
					nurse_id: change.user_id,
					// Analytics keep the stable "First Last" form whatever the department's display order is.
					nurse_name: formatPersonFullName(nurseData, false),
					shift_date: change.shift_date,
					week_index: weekIdx,
					day_no: dayNo,
					shift_no: shiftNo,
					is_auto_scheduler: isAutoScheduler,
				});
			}
		} catch (e) {
			console.error("Error parsing JSON:", e);
		}
	}, [webSocketMessages, acceptNurseChanges, declineNurseChanges, hospitalData, showSnackbar, formatPersonName]);

	const handleRemoveRecommendation = useCallback((index: number) => {
		setRecommendations((prevRecommendations) => prevRecommendations.filter((_, i) => i !== index));
	}, []);

	// The admin side panel docks beside this grid (which makes room for it) and lists these recommendations.
	const sidePanelHost = useMemo<SidePanelHost>(
		() => ({
			recommendations: {
				recommendations,
				isAutoScheduler,
				weekIndex,
				startingDate,
				implementedChanges,
				setImplementedChanges,
				setChosenLevel,
				handleRemoveRecommendation,
				handleOpenDialog,
			},
			onClose: handleDrawerClose,
		}),
		[
			recommendations,
			isAutoScheduler,
			weekIndex,
			startingDate,
			implementedChanges,
			setImplementedChanges,
			handleRemoveRecommendation,
			handleOpenDialog,
			handleDrawerClose,
		],
	);
	useSidePanelHost(sidePanelHost);

	const isWeekNotVisibleToStaff = useMemo<boolean>(() => {
		if (!chosenDepartment.staffScheduleDisplayUntilDate || !hospitalData?.weeks[weekIndex]) {
			return false;
		}
		const visibilityDateOnly = chosenDepartment.staffScheduleDisplayUntilDate.startOf("day");
		const weekDays = hospitalData.weeks[weekIndex].weeklyShifts[0]?.days || [];
		return weekDays.some((day) => {
			const dayDateOnly = day.date.startOf("day");
			return dayDateOnly > visibilityDateOnly;
		});
	}, [chosenDepartment.staffScheduleDisplayUntilDate, hospitalData, weekIndex]);

	useEffect(() => {
		setShowStaffVisibilityBanner(true);
	}, [weekIndex, chosenDepartment.staffScheduleDisplayUntilDate]);

	return (
		<div
			data-testid={"staffing-v1-root"}
			className={Style.staffContainer}
			style={{
				position: "relative",
				overflow: "auto",
				background: isDataInSelfSchedulingPeriod(hospitalData?.weeks[weekIndex]?.weeklyShifts[0]?.days[0]?.date) ? STATUS_BADGE.WARN_BG : SURFACE,
			}}
		>
			<TopBar
				ref={topBarRef}
				sx={{
					borderBottom: `1px solid ${NEUTRAL[300]}`,
					background: SURFACE,
					position: "fixed",
					overflow: "visible",
					top: "var(--app-header-height, 64px)",
					left: 0,
					right: open ? DRAWER_WIDTH_IN_PX : 0,
					zIndex: 999,
					width: "auto",
					boxShadow: `0px 0px 10px 0px ${alpha(INK_BLACK, 0.1)}`,
					padding: "6px 12px",
					transition: (theme) =>
						theme.transitions.create(["width", "right"], {
							easing: theme.transitions.easing.sharp,
							duration: theme.transitions.duration.leavingScreen,
						}),
					paddingTop: "12px",
				}}
			>
				<DateSlider fromWeeklyStaffing />
				<Divider orientation="vertical" sx={{ mr: 1.5, ml: 1, borderColor: NEUTRAL[300], height: "40px" }} />

				<ShiftForm>
					<SystemDropdown
						multiple
						label={t("shifts")}
						hideCheckbox
						multiAsOne
						hideChip
						items={hospitalData?.weeks[weekIndex]?.weeklyShifts.map((shift: Shift) => shift.type)}
						selectedItems={selectedShift}
						onChangeMultiple={(value) => {
							setSelectedShift(value);
							trackEvent("shift_filter_changed", {
								new_value: value,
								shift_name: value.length === shiftsTypeCount ? "Full Day" : value,
							});
						}}
						allOption={t("full.day")}
					/>
				</ShiftForm>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<RoleOrJobSelector
						selectedRoles={selectedRolesForSelector}
						onSelectedRolesChanged={handleSelectedRolesChanged}
						selectedJobIds={selectedJobIds}
						onSelectedJobsChanged={setSelectedJobIds}
						label={t("job")}
						formControlSx={{ minWidth: "200px" }}
					/>
					<EmploymentTypeSelector onSectionsChange={setSelectedSections} />
					{/* Paired with the v2 header's own search field: the two grids are mutually exclusive
					    branches of the same route, so one anchor name covers both. */}
					<StaffAutoComplete
						data-annie-target="staffing-staff-search"
						nurses={nurseOptions}
						value={searchedNurse}
						onChange={(_, value) => setSearchedNurse(value)}
						width="200px"
					/>
				</Box>

				{isHospital && <Availability isSmall={isSmallScreen} setAvailabilityLoaded={setAvailabilityLoaded} />}
				<TopRightPane>
					<DraftMode weekIndex={weekIndex} />
					<SelfSchedulingButton />
					<ScheduleVisibilityButton dataTestId="staffing-schedule-visibility-button" onClick={() => setStaffScheduleVisibilityOpen(true)} />
					{/* Anchored only on the two weekly-Staffing branches: the Day Schedule and the People view render
					    this same panel, and an anchor there would spotlight it on pages the step's copy isn't about. */}
					<StaffingSettingsPanel data-annie-target="staffing-view-settings" mode="weekly" />

					<ScheduleExportButton weekIndex={weekIndex} />

					{/* Anchored only on the two weekly-Staffing branches: the Day Schedule and Synergy Staffing render
					    this same button, and an anchor there would spotlight it on pages the step's copy isn't about. */}
					<DrawerTriggerButton
						data-annie-target="staffing-recommendations"
						open={open}
						notification={notification}
						onClick={() => {
							handleDrawerOpen();
							trackEvent("copilot_button_clicked", {
								week_index: weekIndex,
								recommendation_count: recommendations.length,
							});
						}}
					/>
				</TopRightPane>
			</TopBar>
			<Main open={open} sx={{ marginTop: `${topBarHeight || 60}px` }}>
				{hospitalData?.weeks[weekIndex]?.weeklyShifts
					?.filter((shift: Shift) => selectedShift.includes(shift.type))
					.map((shift: Shift, index: number) => {
						return (
							<div
								key={shift.type}
								style={{
									height: selectedShift.length === shiftsTypeCount ? "calc(100% - min(18vh, 160px))" : "inherit",
									display: "flex",
									alignItems: selectedShift.length === shiftsTypeCount ? "stretch" : "stretch",
									flex: selectedShift.length === shiftsTypeCount ? "1" : "none",
									width: "100%",
								}}
							>
								<div style={{ flex: "0 0 50px" }}>
									<ShiftLabelContainer demo={isHospitalView}>
										<span
											style={{
												transform: "rotate(-90deg)",
												transformOrigin: "center",
												whiteSpace: "nowrap",
												// Demo: white text on the green bar; others keep the dark secondary ink.
												color: isHospitalView ? SURFACE : INK.SECONDARY,
												fontWeight: 500,
												fontSize: "16px",
												position: "absolute",
												left: "-23px",
												width: "100px",
												display: "flex",
												justifyContent: "center",
											}}
										>
											<Grid
												container
												sx={{
													flexWrap: "nowrap",
													flexDirection: "row",
													justifyContent: "center",
													alignItems: "center",
													width: "fit-content",
												}}
											>
												{shift.type === "Day" || shift.type === "Morning" ? <SunIcon /> : <MoonIcon />}
												<span
													style={{
														padding: "5px",
														textAlign: "left",
													}}
												>
													{shift.type} {!(isHospitalView || isHospitalDemo) ? "" : shift.type === "Morning" || shift.type === "Day" ? "(AM)" : "(PM)"}
												</span>
											</Grid>
										</span>
									</ShiftLabelContainer>
								</div>

								<div style={{ flex: 1, width: "calc(100% - 50px)" }}>
									<StaffingBlock
										rolesToDisplay={rolesToDisplay}
										employmentTypesToDisplay={employmentTypesToDisplay}
										selectedJobIds={selectedJobIds}
										key={shift.type}
										shiftNo={hospitalData?.weeks[weekIndex]?.weeklyShifts.findIndex((currentShift: Shift) => currentShift.type === shift.type)}
										isFullDay={selectedShift.length === shiftsTypeCount}
										highlightedNurseId={searchedNurse?.value.id}
										isFirstShiftBlock={index === 0}
									/>
								</div>
							</div>
						);
					})}
			</Main>
			<Dialog
				sx={{
					zIndex: 999999999,
				}}
				open={dialogOpen}
				onClose={() => {
					handleCloseDialog();
					trackEvent("approve_all_dialog_closed_by_backdrop");
				}}
			>
				<DialogTitle>{t("accept.all.changes.confirm")}</DialogTitle>
				<DialogContent>
					<DialogContentText>{t("approve.all.changes.description")}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<HeaderButton
						secondary={true}
						disabled={false}
						onPress={() => {
							handleCloseDialog();
							trackEvent("approve_all_cancelled");
						}}
						state="off"
						text="Cancel"
					/>

					<HeaderButton
						disabled={false}
						onPress={() => {
							handleApproveAll();
							trackEvent("approve_all_confirmed");
						}}
						state="on"
						text="Approve"
					/>
				</DialogActions>
			</Dialog>
			<StaffScheduleVisibilityPopup
				open={staffScheduleVisibilityOpen}
				onClose={() => setStaffScheduleVisibilityOpen(false)}
				departmentId={chosenDepartment?.department_id}
				currentDate={chosenDepartment?.staffScheduleDisplayUntilDate || null}
				scheduleCycleWeeks={chosenDepartment?.scheduleCycleWeeks}
				schedulePublishLeadWeeks={chosenDepartment?.schedulePublishLeadWeeks}
			/>
			{isWeekNotVisibleToStaff && showStaffVisibilityBanner && (
				<Alert
					severity="warning"
					icon={<InfoOutlined />}
					action={
						<IconButton
							size="small"
							onClick={() => setShowStaffVisibilityBanner(false)}
							sx={{ color: STATUS.WARNING.dark, "&:hover": { backgroundColor: alpha(STATUS.WARNING.dark, 0.08) } }}
						>
							<Close fontSize="small" />
						</IconButton>
					}
					sx={{
						position: "fixed",
						bottom: 0,
						left: 0,
						right: open ? `${DRAWER_WIDTH_IN_PX}px` : 0,
						borderRadius: 2,
						zIndex: 1301,
						backgroundColor: STATUS.WARNING.light,
						color: STATUS.WARNING.dark,
						border: `1px solid ${STATUS_BADGE.ORANGE}`,
						transition: "right 0.3s ease",
						"& .MuiAlert-message": { display: "flex", alignItems: "center", width: "100%" },
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<Typography sx={{ fontWeight: 600, color: INK.SECONDARY }}>{t("staff.schedule.visibility.attention")}</Typography>
						<Typography sx={{ color: INK.SECONDARY }}>{t("staff.schedule.visibility.message")}</Typography>
					</Box>
				</Alert>
			)}
		</div>
	);
};

export default Staffing;
